import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ajaxconfig } from '../ajaxconfig';
import { CbtSecurityPhrases, CbtTransferQuestions, CbtDataIdGetCbt, incoming_transfer_data, security_phrase_create, transfer_questions, security_phrase_get } from '../serverurl';

const QuestionTransfer = () => {
  // State for outgoing transfers
  const [sourcePassCode, setSourcePassCode] = useState('');
  const [destinationPassCode, setDestinationPassCode] = useState('');
  const [securityPhrase, setSecurityPhrase] = useState('');
  const [showSecurityField, setShowSecurityField] = useState(false);
  
  // State for incoming transfers
  const [incomingPhrase, setIncomingPhrase] = useState('');
  const [incomingDestinationPassCode, setIncomingDestinationPassCode] = useState('');
  const [incomingTransferStatus, setIncomingTransferStatus] = useState('');
  
  // Shared state
  const [isLoading, setIsLoading] = useState(false);
  const [userPassCodes, setUserPassCodes] = useState([]);
  const [securityPhrases, setSecurityPhrases] = useState([]);
  const [newPhrasePassCode, setNewPhrasePassCode] = useState('');
  const [generatedPhrase, setGeneratedPhrase] = useState('');
  const [isSameAccount, setIsSameAccount] = useState(false);
  const [selectedPasscodeDetails, setSelectedPasscodeDetails] = useState(null);
  const [destinationPasscodeDetails, setDestinationPasscodeDetails] = useState(null);
  const [email, setEmail] = useState('');
  const [activeTab, setActiveTab] = useState('send'); // 'send', 'receive', 'manage'
  
  const config = ajaxconfig();

  const fetchCbtData = async () => {
    try {
      const passcodeData = await axios.get(CbtDataIdGetCbt, config);
      console.log(passcodeData);
      setUserPassCodes(passcodeData.data.data || []);
    } catch (error) {
        console.log(error);
      toast.error('Failed to load data');
      console.error('Fetch error:', error);
    }
  };

  const fetchPhraseData = async () => {
    try {
      const Data = await axios.get(security_phrase_get, config);
      setSecurityPhrases(Data.data);
      console.log(Data);
    } catch (error) {
        console.log(error);
      toast.error(error.message);
      toast.error('Failed to load data');
      console.error('Fetch error:', error);
    }
  };

  // Fetch user's pass codes
  useEffect(() => {
    fetchCbtData();
    fetchPhraseData();
  }, []);

  // Update passcode details when selection changes
  useEffect(() => {
    if (sourcePassCode) {
      const details = userPassCodes.find(p => p.cbt_data_id === sourcePassCode);
      setSelectedPasscodeDetails(details);
    } else {
      setSelectedPasscodeDetails(null);
    }
  }, [sourcePassCode, userPassCodes]);

  // Update destination passcode details
  useEffect(() => {
    if (destinationPassCode) {
      const details = userPassCodes.find(p => p.cbt_data_id === destinationPassCode);
      setDestinationPasscodeDetails(details);
    } else {
      setDestinationPasscodeDetails(null);
    }
  }, [destinationPassCode, userPassCodes]);

  // Check if pass codes belong to same account
  const checkAccountOwnership = async () => {
    if (!sourcePassCode || !destinationPassCode) return;
    
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${transfer_questions}`,
        { sourcePassCode, destinationPassCode },
        config
      );
      console.log(response.data);
      setIsSameAccount(response.data.isSameAccount);
      setShowSecurityField(!response.data.isSameAccount);
    } catch (error) {
        console.log(error);
      toast.error(error.response?.data?.message || 'Ownership check failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle outgoing question transfer
  const handleOutgoingTransfer = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const payload = {
        sourcePassCode,
        destinationPassCode,
        ...(!isSameAccount && { securityPhrase })
      };
      
      const response = await axios.post(transfer_questions, payload, config);
      console.log(response.data);
      toast.success(response.data.message || 'Questions transferred successfully!');
      
      // Reset form
      setSourcePassCode('');
      setDestinationPassCode('');
      setSecurityPhrase('');
      setShowSecurityField(false);
      setSelectedPasscodeDetails(null);
      setDestinationPasscodeDetails(null);
    } catch (error) {
        console.log(error);
      toast.error(error.response?.data?.message || 'Transfer failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle incoming question transfer
  const handleIncomingTransfer = async (e) => {
    e.preventDefault();
    if (!incomingPhrase || !incomingDestinationPassCode) {
      toast.error('Please fill all fields');
      return;
    }
    
    setIsLoading(true);
    setIncomingTransferStatus('Verifying phrase...');
    
    try {
      const response = await axios.post(
        incoming_transfer_data,
        {
          securityPhrase: incomingPhrase,
          destinationPassCode: incomingDestinationPassCode
        },
        config
      );
      
      console.log(response);
      setIncomingTransferStatus('Transfer successful!');
      toast.success('Questions received successfully!');
      
      // Reset form after success
      setIncomingPhrase('');
      setIncomingDestinationPassCode('');
      
      // Refresh passcode data
      setTimeout(() => {
        setIncomingTransferStatus('');
        fetchCbtData();
      }, 3000);
    } catch (error) {
      console.log(error);
      setIncomingTransferStatus('Transfer failed');
      toast.error(error.response?.data?.message || 'Failed to receive questions');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate new security phrase
  const generateSecurityPhrase = async () => {
    if (!newPhrasePassCode || !email) {
      toast.error('Please select a pass code and enter recipient email');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await axios.post(
        security_phrase_create,
        { 
          passCode: newPhrasePassCode,
          email
        },
        config
      );
      console.log(response);
      
      setGeneratedPhrase(response.data.phraseKey);
      setSecurityPhrases([...securityPhrases, response.data]);
      toast.success('Security phrase generated!');
      setEmail('');
    } catch (error) {
        console.log(error);
      toast.error(error.response?.data?.message || 'Failed to generate phrase');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete security phrase
  const deleteSecurityPhrase = async (id) => {
    try {
      setIsLoading(true);
      await axios.delete(`${CbtSecurityPhrases}/${id}`, config);
      setSecurityPhrases(securityPhrases.filter(phrase => phrase.id !== id));
      toast.success('Security phrase deleted');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Delete failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Passcode information card
  const PasscodeInfoCard = ({ passcode, title }) => (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
      <h4 className="text-sm font-medium text-blue-800 mb-2">{title}</h4>
      {passcode ? (
        <div className="space-y-1">
          <div className="flex">
            <span className="text-gray-600 w-24">Name:</span>
            <span className="font-medium">{passcode.cbt_name}</span>
          </div>
          <div className="flex">
            <span className="text-gray-600 w-24">Status:</span>
            <span className="capitalize font-medium">
              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${passcode.cbt_status === 'published' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
              {passcode.cbt_status}
            </span>
          </div>
          <div className="flex">
            <span className="text-gray-600 w-24">Created:</span>
            <span className="font-medium">
              {new Date(passcode.created_at).toLocaleDateString()}
            </span>
          </div>
          <div className="flex">
            <span className="text-gray-600 w-24">ID:</span>
            <code className="font-mono text-sm bg-blue-100 px-1 rounded">{passcode.cbt_data_id}</code>
          </div>
        </div>
      ) : (
        <p className="text-gray-500 text-sm">No information available</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 flex-grow w-full">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Question Transfer Hub</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Securely send and receive questions between pass codes with our transfer system
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="border-b border-gray-200 w-full max-w-4xl">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('send')}
                className={`py-4 px-1 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'send'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Send Questions
              </button>
              <button
                onClick={() => setActiveTab('receive')}
                className={`py-4 px-1 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'receive'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Receive Questions
              </button>
              <button
                onClick={() => setActiveTab('manage')}
                className={`py-4 px-1 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'manage'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Security Phrases
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="w-full">
          {/* Send Questions Tab */}
          {activeTab === 'send' && (
            <div className="bg-white rounded-lg shadow p-6 max-w-4xl mx-auto">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-indigo-100 rounded-full mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-indigo-700">Send Questions</h2>
              </div>
              
              <form onSubmit={handleOutgoingTransfer}>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    From Pass Code (Source)
                  </label>
                  <select
                    value={sourcePassCode}
                    onChange={(e) => setSourcePassCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Select Source Pass Code</option>
                    {userPassCodes.map((passCode) => (
                      <option key={passCode.cbt_data_id} value={passCode.cbt_data_id}>
                        {passCode.cbt_name} ({passCode.cbt_data_id})
                      </option>
                    ))}
                  </select>
                  
                  <PasscodeInfoCard 
                    passcode={selectedPasscodeDetails} 
                    title="Source Passcode Details" 
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    To Pass Code (Destination)
                  </label>
                  <input
                    type="text"
                    value={destinationPassCode}
                    onChange={(e) => setDestinationPassCode(e.target.value)}
                    onBlur={checkAccountOwnership}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter destination pass code"
                    required
                  />
                  
                  <PasscodeInfoCard 
                    passcode={destinationPasscodeDetails} 
                    title="Destination Passcode Details" 
                  />
                </div>

                {showSecurityField && (
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Security Phrase
                    </label>
                    <input
                      type="text"
                      value={securityPhrase}
                      onChange={(e) => setSecurityPhrase(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter security phrase"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Required for cross-account transfers. Ask the recipient for this phrase.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex justify-center py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                    isLoading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending Questions...
                    </span>
                  ) : (
                    'Send Questions'
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Receive Questions Tab */}
          {activeTab === 'receive' && (
            <div className="bg-white rounded-lg shadow p-6 max-w-4xl mx-auto">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-green-100 rounded-full mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-green-700">Receive Questions</h2>
              </div>
              
              <form onSubmit={handleIncomingTransfer}>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Security Phrase
                  </label>
                  <input
                    type="text"
                    value={incomingPhrase}
                    onChange={(e) => setIncomingPhrase(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter security phrase from sender"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Provided by the sender of the questions
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    To Pass Code (Your CBT)
                  </label>
                  <select
                    value={incomingDestinationPassCode}
                    onChange={(e) => setIncomingDestinationPassCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    <option value="">Select Your Pass Code</option>
                    {userPassCodes.map((passCode) => (
                      <option key={passCode.cbt_data_id} value={passCode.cbt_data_id}>
                        {passCode.cbt_name} ({passCode.cbt_data_id})
                      </option>
                    ))}
                  </select>
                  
                  <PasscodeInfoCard 
                    passcode={userPassCodes.find(p => p.cbt_data_id === incomingDestinationPassCode)} 
                    title="Your Passcode Details" 
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex justify-center py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                    isLoading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Receiving Questions...
                    </span>
                  ) : (
                    'Receive Questions'
                  )}
                </button>
                
                {incomingTransferStatus && (
                  <div className={`mt-4 p-3 rounded-md text-center ${
                    incomingTransferStatus.includes('success') 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {incomingTransferStatus}
                  </div>
                )}
              </form>
              
              <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Receiving Questions</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>To receive questions from another account:</p>
                      <ol className="list-decimal pl-5 mt-1 space-y-1">
                        <li>Provide the sender with a security phrase from the Security Phrases tab</li>
                        <li>They'll send you the phrase after initiating the transfer</li>
                        <li>Enter the phrase here and select your destination passcode</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Phrases Tab */}
          {activeTab === 'manage' && (
            <div className="bg-white rounded-lg shadow p-6 max-w-6xl mx-auto">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-purple-100 rounded-full mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-purple-700">Security Phrase Management</h2>
              </div>
              
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3">Generate New Phrase</h3>
                
                <div className="mb-3">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Recipient Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    placeholder="recipient@example.com"
                  />
                </div>
                
                <div className="mb-3">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Pass Code to Share
                  </label>
                  <select
                    value={newPhrasePassCode}
                    onChange={(e) => setNewPhrasePassCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Select Pass Code</option>
                    {userPassCodes.map((passCode) => (
                      <option key={passCode.cbt_data_id} value={passCode.cbt_data_id}>
                        {passCode.cbt_name} ({passCode.cbt_data_id})
                      </option>
                    ))}
                  </select>
                </div>
                
                <button
                  onClick={generateSecurityPhrase}
                  disabled={isLoading || !newPhrasePassCode || !email}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    isLoading || !newPhrasePassCode || !email
                      ? 'bg-gray-400'
                      : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                >
                  {isLoading ? 'Generating...' : 'Generate Security Phrase'}
                </button>
                
                {generatedPhrase && (
                  <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-md">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-purple-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-purple-800">Security Phrase Created!</h3>
                        <div className="mt-2 text-sm text-purple-700">
                          <p className="mb-2">Share this phrase with the sender:</p>
                          <div className="flex items-center justify-between p-3 bg-white rounded border border-purple-200">
                            <code className="font-mono">{generatedPhrase}</code>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(generatedPhrase);
                                toast.info('Phrase copied to clipboard');
                              }}
                              className="ml-2 px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-sm"
                            >
                              Copy
                            </button>
                          </div>
                          <p className="mt-2 text-xs">
                            This phrase allows others to transfer questions to your passcode
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Your Security Phrases</h3>
                {securityPhrases.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No security phrases</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Create a security phrase to allow others to transfer questions to your passcodes
                    </p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-md max-h-[500px] overflow-y-auto overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pass Code
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            CBT Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Recipient Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Phrase Key
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {securityPhrases.map((phrase) => {
                          const passcode = userPassCodes.find(
                            (p) => p.cbt_data_id === phrase.cbt_pass_code
                          );
                          return (
                            <tr key={phrase.id} className="hover:bg-gray-50">
                              <td className="px-4 py-4 text-sm font-medium text-gray-900">
                                {phrase.cbt_pass_code}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-500">
                                {passcode ? passcode.cbt_name : "N/A"}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-500">
                                {phrase.email}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-500">
                                <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                  {phrase.phrase_key}
                                </code>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-500">
                                <button
                                  onClick={() => deleteSecurityPhrase(phrase.id)}
                                  disabled={isLoading}
                                  className="text-red-600 hover:text-red-900 font-medium"
                                >
                                  Revoke
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 bg-blue-50 border-l-4 border-blue-400 p-4 rounded max-w-7xl mx-auto w-full">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">How Question Transfer Works</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc pl-5 space-y-1">
                  <li><span className="font-medium">Send Questions:</span> Transfer questions from your passcode to another account</li>
                  <li><span className="font-medium">Receive Questions:</span> Accept questions from other accounts using their security phrase</li>
                  <li><span className="font-medium">Security Phrase:</span> Generate phrases to authorize incoming transfers to your passcodes</li>
                  <li><span className="font-medium">Safety:</span> All transfers are securely encrypted and verified</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      </div>
 
 {/* Improved Footer */}
<footer className="bg-gray-900 text-white w-full mt-16">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
      {/* Brand Section */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold flex items-center space-x-2">
          {/* Logo Placeholder */}
          <img 
            src="/asset/logo.png" 
            alt="CBT Host Logo" 
            className="h-8 w-auto"
          />
          <span className="text-white">CBTHost.com</span>
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed">
          Making learning and assessment accessible to everyone, everywhere with innovative technology solutions.
        </p>
        <div className="flex space-x-4 pt-2">
          <a href="#" className="no-underline text-gray-400 hover:text-white transition-colors duration-300">
            <span className="sr-only">Facebook</span>
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">...</svg>
          </a>
          <a href="#" className="no-underline text-gray-400 hover:text-white transition-colors duration-300">
            <span className="sr-only">Twitter</span>
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">...</svg>
          </a>
          <a href="#" className="no-underline text-gray-400 hover:text-white transition-colors duration-300">
            <span className="sr-only">Instagram</span>
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">...</svg>
          </a>
          <a href="#" className="no-underline text-gray-400 hover:text-white transition-colors duration-300">
            <span className="sr-only">LinkedIn</span>
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">...</svg>
          </a>
        </div>
      </div>

      {/* Resources */}
      <div>
        <h3 className="text-sm font-semibold text-white tracking-wider uppercase">Resources</h3>
        <ul className="mt-4 space-y-3">
          <li><a href="https://docs.cbthost.com" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">Documentation</a></li>
          <li><a href="https://cbthost.com/guides" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">Guides</a></li>
          <li><a href="https://cbthost.com/blog" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">Blog</a></li>
          <li><a href="https://cbthost.com/support" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">Support</a></li>
        </ul>
      </div>

      {/* Company */}
      <div>
        <h3 className="text-sm font-semibold text-white tracking-wider uppercase">Company</h3>
        <ul className="mt-4 space-y-3">
          <li><a href="https://cbthost.com/about-us" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">About Us</a></li>
          <li><a href="https://cbthost.com/careers" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">Careers</a></li>
          <li><a href="https://cbthost.com/contact" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">Contact</a></li>
          <li><a href="https://cbthost.com/partners" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">Partners</a></li>
        </ul>
      </div>

      {/* Legal */}
      <div>
        <h3 className="text-sm font-semibold text-white tracking-wider uppercase">Legal</h3>
        <ul className="mt-4 space-y-3">
          <li><a href="https://cbthost.com/privacy-policy" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">Privacy Policy</a></li>
          <li><a href="https://cbthost.com/tos" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">Terms of Service</a></li>
          <li><a href="https://cbthost.com/cookies" className="no-underline text-gray-400 hover:text-white transition-colors duration-300 text-sm">Cookie Policy</a></li>
        </ul>
      </div>
    </div>

    {/* Bottom Section */}
    <div className="border-t border-gray-800 pt-8 mt-8 text-center text-gray-400 text-sm">
      <p>&copy; {new Date().getFullYear()} CBTHost.com. All rights reserved.</p>
    </div>
  </div>
</footer>




    </div>
  );
};

export default QuestionTransfer;